import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Building2, CreditCard, FileText, Activity,
  Package, Wallet as WalletIcon, FileSignature, ToggleLeft, ToggleRight,
  Save, Loader2,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";

const CUSTOMER_TYPE_BADGE = {
  RETAIL: "bg-blue-50 text-blue-700 border-blue-200",
  B2C: "bg-purple-50 text-purple-700 border-purple-200",
  B2B: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-brand-500" />}
        <h3 className="font-heading text-sm font-bold text-slate-900 uppercase tracking-wide">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, value, name, editing, onChange, type = "text", readonly }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50 last:border-0">
      <div className="text-xs text-slate-500 uppercase font-semibold">{label}</div>
      {editing && !readonly ? (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(name, e.target.value)}
          data-testid={`customer-field-${name}`}
          className="col-span-2 text-sm text-slate-800 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-brand-500"
        />
      ) : (
        <div className="col-span-2 text-sm text-slate-800">{value || "—"}</div>
      )}
    </div>
  );
}

export default function CustomerProfile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [creditEnabled, setCreditEnabled] = useState(false);
  const [creditLimit, setCreditLimit] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/v2/customers/profile/${id}`);
      setProfile(r.data);
      setForm({
        company_name: r.data.company_name || "",
        contact_person: r.data.contact_person || r.data.user?.name || "",
        mobile: r.data.mobile || r.data.user?.phone || "",
        email: r.data.email || r.data.user?.email || "",
        gst_number: r.data.gst_number || "",
        pan_number: r.data.pan_number || "",
        address_line1: r.data.address_line1 || "",
        address_line2: r.data.address_line2 || "",
        city: r.data.city || "",
        state: r.data.state || "",
        pincode: r.data.pincode || "",
        billing_cycle: r.data.billing_cycle || "MONTHLY",
        platform_fee_enabled: r.data.platform_fee_enabled !== false,
      });
      setCreditEnabled(!!r.data.user?.wallet_credit_enabled);
      setCreditLimit(r.data.user?.credit_limit || 0);
    } catch (e) {
      toast.error("Failed to load customer profile");
    }

    // Try to fetch rate agreement (optional)
    try {
      const ag = await api.get("/v2/rate-agreements", { params: { customer_id: id } });
      const list = ag.data?.items || ag.data || [];
      const active = (Array.isArray(list) ? list : []).find((a) => a.status === "ACTIVE") || (Array.isArray(list) ? list[0] : null);
      setAgreement(active || null);
    } catch { /* optional */ }

    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const onFieldChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/v2/customers/profile/${id}`, form);
      toast.success("Profile updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleCredit = async () => {
    const next = !creditEnabled;
    try {
      await api.put(`/v2/wallets/${id}/credit-toggle`, {
        enabled: next, reason: "admin toggle",
        credit_limit: next ? (creditLimit || 0) : undefined,
      });
      toast.success(`Credit wallet ${next ? "enabled" : "disabled"}`);
      setCreditEnabled(next);
      load();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-slate-500">Customer not found.</p>
          <Link to="/admin/customers" className="text-brand-500 text-sm font-semibold">← Back to customers</Link>
        </div>
      </DashboardLayout>
    );
  }

  const wallet = profile.wallet_balance ?? profile.user?.wallet_balance ?? 0;
  const accountMode = profile.user?.account_mode || (creditEnabled ? "CREDIT" : "PREPAID");
  const customerType = profile.customer_type || "RETAIL";

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/customers" data-testid="customer-back-btn"
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                {profile.company_name || profile.contact_person || "Customer"}
              </h1>
              <p className="text-slate-500 text-xs">
                ID: <span className="font-mono">{id}</span>
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {!editing ? (
              <button onClick={() => setEditing(true)} data-testid="customer-edit-btn"
                className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded">Edit</button>
            ) : (
              <>
                <button onClick={() => { setEditing(false); load(); }} className="px-3 py-2 border text-sm rounded">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="customer-save-btn"
                  className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded flex items-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </>
            )}
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card title="Business Info" icon={Building2} testid="customer-card-business">
            <Field label="Company Name" name="company_name" value={form.company_name} editing={editing} onChange={onFieldChange} />
            <Field label="Contact Person" name="contact_person" value={form.contact_person} editing={editing} onChange={onFieldChange} />
            <Field label="Mobile" name="mobile" value={form.mobile} editing={editing} onChange={onFieldChange} />
            <Field label="Email" name="email" value={form.email} editing={editing} onChange={onFieldChange} readonly />
            <Field label="GSTIN" name="gst_number" value={form.gst_number} editing={editing} onChange={onFieldChange} />
            <Field label="PAN" name="pan_number" value={form.pan_number} editing={editing} onChange={onFieldChange} />
            <Field label="Address 1" name="address_line1" value={form.address_line1} editing={editing} onChange={onFieldChange} />
            <Field label="Address 2" name="address_line2" value={form.address_line2} editing={editing} onChange={onFieldChange} />
            <div className="grid grid-cols-3 gap-2 mt-1">
              <Field label="City" name="city" value={form.city} editing={editing} onChange={onFieldChange} />
              <Field label="State" name="state" value={form.state} editing={editing} onChange={onFieldChange} />
              <Field label="Pincode" name="pincode" value={form.pincode} editing={editing} onChange={onFieldChange} />
            </div>
          </Card>

          <Card title="Account Settings" icon={CreditCard} testid="customer-card-account">
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50">
              <div className="text-xs text-slate-500 uppercase font-semibold">Customer Type</div>
              <div className="col-span-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${CUSTOMER_TYPE_BADGE[customerType] || ""}`}>
                  {customerType}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50">
              <div className="text-xs text-slate-500 uppercase font-semibold">Wallet Type</div>
              <div className="col-span-2 text-sm font-semibold text-slate-800">{accountMode}</div>
            </div>
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50">
              <div className="text-xs text-slate-500 uppercase font-semibold">Wallet Balance</div>
              <div className="col-span-2 text-sm font-bold text-emerald-700">₹{(wallet || 0).toFixed(2)}</div>
            </div>
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50 items-center">
              <div className="text-xs text-slate-500 uppercase font-semibold">Credit Wallet</div>
              <div className="col-span-2">
                <button onClick={toggleCredit} data-testid="customer-credit-toggle"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                  {creditEnabled ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-slate-300" />}
                  {creditEnabled ? "Enabled" : "Disabled"}
                </button>
              </div>
            </div>
            {creditEnabled && (
              <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50 items-center">
                <div className="text-xs text-slate-500 uppercase font-semibold">Credit Limit (₹)</div>
                <div className="col-span-2">
                  <input type="number" value={creditLimit}
                    onChange={(e) => setCreditLimit(parseFloat(e.target.value) || 0)}
                    onBlur={async () => {
                      try {
                        await api.put(`/v2/wallets/${id}/credit-toggle`, {
                          enabled: true, credit_limit: creditLimit,
                        });
                        toast.success("Credit limit updated");
                      } catch { toast.error("Update failed"); }
                    }}
                    data-testid="customer-credit-limit"
                    className="text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-brand-500 w-32" />
                </div>
              </div>
            )}
            <Field label="Billing Cycle" name="billing_cycle" value={form.billing_cycle} editing={editing} onChange={onFieldChange} />
            <div className="grid grid-cols-3 gap-2 py-2 items-center">
              <div className="text-xs text-slate-500 uppercase font-semibold">Platform Fee</div>
              <div className="col-span-2">
                {editing ? (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.platform_fee_enabled}
                      onChange={(e) => onFieldChange("platform_fee_enabled", e.target.checked)}
                      data-testid="customer-platform-fee-toggle" />
                    Enabled
                  </label>
                ) : (
                  <span className="text-sm font-semibold text-slate-700">{form.platform_fee_enabled ? "Enabled" : "Disabled"}</span>
                )}
              </div>
            </div>
          </Card>

          <Card title="Rate Agreement Summary" icon={FileText} testid="customer-card-agreement">
            {agreement ? (
              <>
                <Field label="FM Rate (₹/kg)" value={agreement.fm_lm_per_kg || "—"} />
                <Field label="LM Rate (₹/kg)" value={agreement.fm_lm_per_kg || "—"} />
                <Field label="MM Override" value={agreement.mm_override || "Use default"} />
                <Field label="LR Charge (₹)" value={agreement.lr_charge ?? 250} />
                <Field label="Counter-Drop FM Waiver" value={agreement.counter_drop_fm_waiver ? "Yes" : "No"} />
                <Field label="Counter-Collect LM Waiver" value={agreement.counter_collect_lm_waiver ? "Yes" : "No"} />
                <Field label="3PL Markup %" value={agreement.tpl_markup_pct ?? "Default 20%"} />
                <Link to={`/admin/rate-agreements?customer_id=${id}`}
                  className="text-brand-500 text-xs font-semibold mt-2 inline-block">View Full Agreement →</Link>
              </>
            ) : (
              <p className="text-sm text-slate-400 italic">No active rate agreement.
                <Link to={`/admin/rate-agreements?customer_id=${id}`} className="text-brand-500 ml-1">Create →</Link>
              </p>
            )}
          </Card>

          <Card title="Activity" icon={Activity} testid="customer-card-activity">
            <Field label="Total Shipments" value={profile.total_shipments ?? "—"} />
            <Field label="Total Spend (₹)" value={profile.total_spend?.toFixed?.(2) ?? "—"} />
            <Field label="Last Booking" value={profile.last_booking_date ? new Date(profile.last_booking_date).toLocaleDateString("en-IN") : "—"} />
            <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-50">
              <div className="text-xs text-slate-500 uppercase font-semibold">KYC Status</div>
              <div className="col-span-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                  profile.kyc_status === "VERIFIED" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  profile.kyc_status === "REJECTED" ? "bg-red-50 text-red-700 border-red-200" :
                  "bg-amber-50 text-amber-700 border-amber-200"
                }`}>
                  {profile.kyc_status || "PENDING"}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Action buttons row */}
        <div className="flex gap-3 flex-wrap">
          <Link to={`/admin/shipments?customer_id=${id}`} data-testid="customer-view-shipments"
            className="px-4 py-2 border border-slate-200 text-sm font-semibold rounded inline-flex items-center gap-2 hover:bg-slate-50">
            <Package className="w-4 h-4" /> View Shipments
          </Link>
          <Link to={`/admin/wallet-ledger-v2?customer_id=${id}`} data-testid="customer-view-wallet"
            className="px-4 py-2 border border-slate-200 text-sm font-semibold rounded inline-flex items-center gap-2 hover:bg-slate-50">
            <WalletIcon className="w-4 h-4" /> View Wallet
          </Link>
          <Link to={`/admin/rate-agreements?customer_id=${id}`} data-testid="customer-view-agreement"
            className="px-4 py-2 border border-slate-200 text-sm font-semibold rounded inline-flex items-center gap-2 hover:bg-slate-50">
            <FileSignature className="w-4 h-4" /> View Agreement
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
